// MACHINERY V2 (Stroje) — Dokumenty tab: downloads and the three mutations
// (M2.5).
//
// Split out of DocumentsTabV2.tsx for the 300-line cap. That file owns the
// fetch, the states and the pixels; this owns what the buttons DO.
//
// "STIAHNUŤ VŠETKY" IS DELIBERATELY SLOW. 500 ms between files, because a
// browser silently drops a burst of programmatic downloads. v1 does the same
// (ManualsAndSoftware.tsx:46-54, `await new Promise(resolve =>
// setTimeout(resolve, 500))` at :53, inside the loop) and the artboard annotates it.
// The loop is cancelled when the caller says it is no longer mounted — v1 has
// no such guard and keeps firing anchors into a page the user has left.
//
// FAILURES ARE NO LONGER SILENT. §6 records "error stored, never rendered", and
// `deleteDocument` has NO extraReducer case at all
// (customerMachineDocumentsSlice.ts — its builder chain ends with
// `changeDocument.rejected` and never mentions deleteDocument), so a failed delete produced nothing anywhere. All
// three mutations now report through ToastV2, which ProfileBody mounts once.
//
// `submitUpload` RETURNS THE FAILURES — IT DOES NOT SWALLOW THEM (M2.5 review
// finding R2). It used to catch every per-file rejection internally and always
// resolve `void`, so the dialog could not tell one failed POST from five and
// closed regardless, destroying the queue AND the hand-edited per-file names on
// a backend 500. The indices of the documents that failed are handed back so
// DocsUploadDialogV2 can keep exactly those rows, mark them, and let the user
// retry or cancel deliberately. The toast stays: it reports the outcome of a
// per-file loop that also partially SUCCEEDED, which the dialog alone cannot say.
//
// THE DOWNLOAD ANCHOR CARRIES THE REAL FILENAME (M2.5 review finding C6). On a
// `data:` URI an empty `download` attribute makes the browser invent a name
// (download.pdf) and the document name in hand is thrown away. v1 dodges it on
// the row action by opening a tab instead (components/manualsAndSoftware/
// DocumentItem.tsx:73 `link.target = "_blank"`) and has the same defect on the
// bulk loop (ManualsAndSoftware.tsx:50 `a.setAttribute("download", "")`) — both
// personally read in this worktree, ticket process rule (a). Passing
// `file.name` fixes both paths and is not a regression from either.
import { useCallback } from "react";

import { useAppDispatch } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  changeDocument,
  createDocument,
  deleteDocument,
} from "../../../customerMachineDocumentsSlice";
import { setError, setSuccess } from "../../../customerMachinerySlice";
import {
  ChangeDocument,
  Document,
  DocumentFile,
} from "../../../models/machineDocumentsModel";
import useV2Confirm from "../../feedback/useV2Confirm";

const DOWNLOAD_GAP_MS = 500;

/** C6: the attribute VALUE is the point — see the header. */
const clickDownload = (file: DocumentFile) => {
  const a = window.document.createElement("a");
  a.href = file.content;
  a.setAttribute("download", file.name || "");
  a.click();
  a.remove();
};

export interface DocsTabActions {
  download: (doc: Document) => void;
  downloadAll: (documents: Document[]) => Promise<void>;
  /** Resolves with the INDICES (into `models`) whose POST failed. Empty means
   *  every file landed — the only case in which the dialog may close (R2). */
  submitUpload: (models: ChangeDocument[]) => Promise<number[]>;
  submitRename: (documentId: string, model: ChangeDocument) => Promise<void>;
  requestDelete: (doc: Document) => void;
}

export const useDocsTabActions = (
  machineId: string,
  reload: () => void,
  /** Reads false once the tab unmounts — stops the staggered download loop. */
  isAlive: () => boolean
): DocsTabActions => {
  const dispatch = useAppDispatch();
  const confirm = useV2Confirm();
  const { translate } = useTranslation("myMachinery");

  const download = useCallback((doc: Document) => {
    if (doc.file?.content) clickDownload(doc.file);
  }, []);

  const downloadAll = useCallback(
    async (documents: Document[]) => {
      const files = documents.map((d) => d.file).filter((f) => !!f?.content) as DocumentFile[];
      for (const file of files) {
        if (!isAlive()) return;
        clickDownload(file);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_GAP_MS));
      }
    },
    [isAlive]
  );

  const submitUpload = useCallback(
    async (models: ChangeDocument[]): Promise<number[]> => {
      const failures: number[] = [];
      for (let i = 0; i < models.length; i += 1) {
        try {
          // Sequential: one POST per document, in order, so a partial failure is
          // reportable per FILE (R2) instead of as an unordered race.
          // eslint-disable-next-line no-await-in-loop
          await dispatch(createDocument({ machineId, model: models[i] })).unwrap();
        } catch {
          failures.push(i);
        }
      }
      reload();
      dispatch(
        failures.length > 0
          ? setError(
              translate("myMachinery:v2.tabs.docs.uploadFailed", { count: failures.length })
            )
          : setSuccess(translate("myMachinery:v2.tabs.docs.uploaded", { count: models.length }))
      );
      return failures;
    },
    [dispatch, machineId, reload, translate]
  );

  const submitRename = useCallback(
    async (documentId: string, model: ChangeDocument) => {
      try {
        await dispatch(changeDocument({ documentId, model })).unwrap();
        dispatch(setSuccess(translate("myMachinery:v2.tabs.docs.renamed")));
      } catch {
        dispatch(setError(translate("myMachinery:v2.tabs.docs.saveFailed")));
      }
      reload();
    },
    [dispatch, reload, translate]
  );

  const requestDelete = useCallback(
    (doc: Document) => {
      const opened = confirm({
        title: translate("myMachinery:deleteDocumentTitle"),
        text: translate("myMachinery:deleteDocumentText", { name: doc.name }),
        confirmButton: translate("myMachinery:yesDelete"),
        closeButton: translate("common:common.no"),
        severity: "error",
        onConfirm: async () => {
          try {
            await dispatch(deleteDocument(doc.id)).unwrap();
            dispatch(
              setSuccess(translate("myMachinery:v2.tabs.docs.deleted", { name: doc.name }))
            );
          } catch {
            dispatch(setError(translate("myMachinery:v2.tabs.docs.deleteFailed")));
          }
          reload();
        },
      });
      // A refused confirm must be spoken aloud — see useV2Confirm's header.
      if (!opened) dispatch(setError(translate("myMachinery:delete.confirmBusy")));
    },
    [confirm, dispatch, reload, translate]
  );

  return { download, downloadAll, submitUpload, submitRename, requestDelete };
};

export default useDocsTabActions;
